import { Turnstile } from '@marsidev/react-turnstile';
import type {
  ActionFunctionArgs,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction
} from '@remix-run/node';
import { Form, Link, useFetcher, useLoaderData } from '@remix-run/react';
import FilepondCSS from 'filepond/dist/filepond.min.css?url';
import freeEmailDomains from 'free-email-domains';
import { TriangleAlert } from 'lucide-react';
import psl from 'psl';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as v from 'valibot';
import { serverOnly$ } from 'vite-env-only/macros';
import { SmallLoader } from '~/components/loader';
import { Logo } from '~/components/logo';
import { PDFUploadWidget } from '~/components/pdf-upload-widget';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Boundary } from '~/components/ui/error';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Toaster } from '~/components/ui/toaster';
import { useToast } from '~/components/ui/use-toast';
import { updateOtpById } from '~/models/otp/update-otp';
import { getUserByEmail } from '~/models/user/get-user';
import { requireNotAuthenticated } from '~/services/auth/config.server';
import { commitSession, getSession } from '~/services/auth/session.server';
import { sendOtpToUser } from '~/services/auth/totp.server';
import { verifyTurnstile } from '~/services/turnstile/config.server';
import { bannedDomains } from '~/utils/banned-domains';
import { getClientIPAddress } from '~/utils/get-client-ip-address.server';
import { redirect } from '~/utils/redirect.server';
import { useFormState } from '~/utils/use-form-state';
import { formatIssues, parseFormData } from '~/utils/validation';

export const meta: MetaFunction = () => [
  { title: 'tia - Register' },
  {
    description: 'Register for tia'
  }
];

export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: FilepondCSS }];
};

export const ErrorBoundary = Boundary;

export async function loader({ request, response }: LoaderFunctionArgs) {
  await requireNotAuthenticated(request, response);

  const session = await getSession(request.headers.get('Cookie'));

  const email = session.get('email') as string | undefined;
  const error = session.get('authError') as string | undefined;
  const name = session.get('name') as string | undefined;
  const companyName = session.get('companyName') as string | undefined;
  const tempPDF = session.get('tempPDF') as string | undefined;
  const docsUrl = session.get('docsUrl') as string | undefined;
  const idempotencyKey = crypto.randomUUID();
  session.set('idempotencyKey', idempotencyKey);

  response?.headers.append('Set-Cookie', await commitSession(session));

  return {
    idempotencyKey,
    turnstileKey: process.env.CLOUDFLARE_TURNSTILE_KEY!,
    email: email || '',
    error,
    name: name || '',
    companyName: companyName || '',
    docsUrl: docsUrl || '',
    tempPDF: tempPDF || ''
  };
}

const Schema = serverOnly$(
  v.object({
    name: v.pipe(v.string(), v.minLength(1), v.maxLength(255)),
    email: v.pipe(v.string(), v.email(), v.maxLength(500)),
    companyName: v.pipe(v.string(), v.minLength(1), v.maxLength(255)),
    docsUrl: v.optional(v.pipe(v.string(), v.url(), v.maxLength(1024))),
    timezoneOffset: v.optional(v.string()),
    dataSourceType: v.picklist(['uploadedPDF', 'other']),
    'cf-turnstile-response': v.optional(v.string())
  })
);

export async function action({ request }: ActionFunctionArgs) {
  const result = v.safeParse(Schema!, parseFormData(Schema!, await request.formData()));
  const session = await getSession(request.headers.get('Cookie'));

  if (!session.get('idempotencyKey')) {
    session.flash(
      'authError',
      'Session expired. Please refresh the page and try again. (You may need to enable cookies)'
    );
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  }

  if (!result.success) {
    session.flash('authError', formatIssues(result.issues));
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  }

  const { email, name, companyName, docsUrl, timezoneOffset, dataSourceType } =
    result.output;

  let outcome;
  if (process.env.CI || process.env.PLAYWRIGHT_TEST_BASE_URL) {
    outcome = { success: true };
  } else {
    outcome = await verifyTurnstile({
      turnstileResponse: result.output['cf-turnstile-response'],
      ip: getClientIPAddress(request),
      idempotencyKey: session.get('idempotencyKey') as string
    });
  }

  if (!outcome.success) {
    session.flash(
      'authError',
      'Failed to verify you are human. Please refresh the page and try again.'
    );
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  }

  const emailDomain = email.split('@')[1];
  if (freeEmailDomains.includes(emailDomain)) {
    session.flash('authError', 'Please use your work email to create an account');
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  }

  if (docsUrl) {
    let parsedDocsUrl;
    try {
      parsedDocsUrl = new URL(docsUrl);
    } catch (e) {
      session.flash('authError', 'Invalid URL');
      throw redirect('/register', 302, {
        'Set-Cookie': await commitSession(session)
      });
    }

    const parsedDocsUrlDomain = psl.parse(parsedDocsUrl.hostname);

    if (
      (parsedDocsUrlDomain?.hasOwnProperty('domain') &&
        bannedDomains.includes((parsedDocsUrlDomain as any).domain)) ||
      bannedDomains.includes(parsedDocsUrl.hostname)
    ) {
      session.flash(
        'authError',
        "The URL you provided is not allowed, please provide the URL to your company's documentation."
      );
      throw redirect('/register', 302, {
        'Set-Cookie': await commitSession(session)
      });
    }

    // is IP or IPv6?
    if (
      parsedDocsUrl.hostname.match(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/) ||
      parsedDocsUrl.hostname.match(/^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/)
    ) {
      session.flash('authError', 'Please provide a URL, not an IP address.');
      throw redirect('/register', 302, {
        'Set-Cookie': await commitSession(session)
      });
    }

    session.set('docsUrl', docsUrl);
  }

  if (dataSourceType === 'other' && !docsUrl) {
    session.flash('authError', "Please provide a URL to your company's documentation.");
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  } else if (dataSourceType === 'uploadedPDF' && !session.get('tempPDF')) {
    session.flash('authError', "Please upload a PDF of your company's documentation.");
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  } else session.set('dataSourceType', dataSourceType);
  session.set('email', email);
  session.set('name', name);
  session.set('companyName', companyName);
  session.set('timezoneOffset', timezoneOffset);
  session.unset('authError');

  const existingOtp = session.get('otp') as string | undefined;
  if (existingOtp) {
    // invalidate any existing otp
    await updateOtpById(existingOtp, { active: false });
  }

  const existingUser = await getUserByEmail(email);

  if (existingUser) {
    session.set('email', email);
    session.flash('authError', 'Email exists.');
    throw redirect('/register', 302, {
      'Set-Cookie': await commitSession(session)
    });
  }

  const otp = await sendOtpToUser('register', name, email);
  session.set('otp', otp.id);

  const cookie = await commitSession(session, { maxAge: 60 * 10 });

  return redirect('/register/confirm', 302, {
    'Set-Cookie': cookie
  });
}

export default function Register() {
  let loaderData = useLoaderData<typeof loader>();
  const email = loaderData.email;
  const name = loaderData.name;
  const companyName = loaderData.companyName;
  const turnstileKey = loaderData.turnstileKey;
  const idempotencyKey = loaderData.idempotencyKey;
  const tempPDF = loaderData.tempPDF;
  const docsUrl = loaderData.docsUrl;

  let error = loaderData.error as React.ReactNode | string | undefined;

  const formState = useFormState();

  const [timezoneOffset, setTimezoneOffset] = useState(0);

  useEffect(() => {
    setTimezoneOffset(new Date().getTimezoneOffset());
  }, []);

  const ref = useRef<HTMLFormElement>(null);

  const uploadSignatureFetcher = useFetcher<{
    success: true;
    fields: Record<string, string>;
    url: string;
  }>();

  const saveTempPDFFetcher = useFetcher<
    | { success: true; tempPDF: string }
    | {
        success: false;
        error: string;
      }
  >();

  const [showingPDFForm, setShowPDFForm] = useState(!!tempPDF);
  const [provedHuman, setProvedHuman] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (showingPDFForm && provedHuman) {
      if (ref.current && uploadSignatureFetcher.state !== 'loading') {
        const formData = new FormData(ref.current);

        uploadSignatureFetcher.load(
          `/api/upload-temp-pdf?cf-turnstile-response=${encodeURIComponent(formData.get('cf-turnstile-response') as string)}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingPDFForm, provedHuman]);

  const showPDFForm = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (provedHuman) {
        setShowPDFForm(true);
      } else {
        toast({
          variant: 'destructive',
          title: 'Please wait a moment',
          description:
            'We need to verify you are human before you can proceed to upload a PDF.'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provedHuman]
  );

  const hidePDFForm = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowPDFForm(false);
  }, []);

  const saveUploadedPDF = useCallback((key: string) => {
    saveTempPDFFetcher.load(`/api/upload-temp-pdf?key=${encodeURIComponent(key)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    error =
      error === 'Email exists.' ? (
        <>
          A user already exists with the provided email address. Would you like to{' '}
          <Link to="/login" className="underline">
            log in
          </Link>
          ?
        </>
      ) : (
        error
      );
  }

  return (
    <>
      <div className="flex">
        <div className="flex-1 p-10">
          <Logo className="mx-auto mb-8 h-8" />
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Create an account</h1>
            <p className="text-sm text-muted-foreground">
              Enter your information below to create your account
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error || 'An error has occurred. Please try again.'}
              </AlertDescription>
            </Alert>
          )}

          <Form ref={ref} method="post">
            <fieldset disabled={formState.submitting} className="space-y-4">
              <input type="hidden" name="timezoneOffset" value={timezoneOffset} />
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  name="name"
                  id="name"
                  placeholder="John Doe"
                  required
                  autoFocus
                  defaultValue={name}
                />
              </div>
              <div>
                <Label htmlFor="company-name">Company Name</Label>
                <Input
                  name="companyName"
                  id="company-name"
                  placeholder="Acme Inc."
                  required
                  defaultValue={companyName}
                />
              </div>
              <div>
                <Label htmlFor="work-email">
                  Work Email{' '}
                  <Popover>
                    <PopoverTrigger className="text-primary">(Why?)</PopoverTrigger>
                    <PopoverContent className="w-72 text-sm text-black">
                      We require a work email address to create an account to prevent
                      abuse of our document ingestion features.
                      <br />
                      <br />
                      This simple verification step helps us ensure responsible use of our
                      system while avoiding the need for more cumbersome methods like
                      credit card verification.
                    </PopoverContent>
                  </Popover>
                </Label>
                <Input
                  name="email"
                  id="work-email"
                  placeholder="john.doe@acme.com"
                  required
                  type="email"
                  defaultValue={email}
                />
              </div>

              {showingPDFForm ? (
                <div>
                  <input type="hidden" name="dataSourceType" value="uploadedPDF" />
                  <Label>Documentation PDF (under 20MB, 20 pages max)</Label>

                  {uploadSignatureFetcher.data?.url &&
                  uploadSignatureFetcher.data?.fields ? (
                    <div>
                      {saveTempPDFFetcher.data?.success === false ? (
                        <Alert variant="destructive" className="mt-4">
                          <TriangleAlert className="h-4 w-4" />
                          <AlertTitle>Error</AlertTitle>
                          <AlertDescription>
                            {saveTempPDFFetcher.data.error ||
                              'An error has occurred. Please try again.'}
                          </AlertDescription>
                        </Alert>
                      ) : null}
                      <PDFUploadWidget
                        initialFileUrl={tempPDF}
                        isTemporary
                        url={uploadSignatureFetcher.data.url}
                        fields={uploadSignatureFetcher.data.fields}
                        onUploaded={saveUploadedPDF}
                      />
                      <span className="text-sm">
                        You can also{' '}
                        <Button
                          type="button"
                          variant="link"
                          className="p-0"
                          onClick={(e) => hidePDFForm(e)}>
                          enter a URL
                        </Button>{' '}
                        instead.
                      </span>
                    </div>
                  ) : (
                    <div className="p-2">
                      <SmallLoader />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <input type="hidden" name="dataSourceType" value="other" />
                  <Label htmlFor="docs-url">
                    Link to your documentation, help center, or website
                  </Label>
                  <Input
                    name="docsUrl"
                    id="docs-url"
                    placeholder="https://mycompany.com/docs"
                    required
                    type="url"
                    defaultValue={docsUrl || ''}
                  />
                  <p className="mt-2 text-sm">
                    <span className="text-muted-foreground">
                      This URL will be used to ingest and index your content, enabling tia
                      to provide intelligent responses based on your specific business
                      knowledge.
                    </span>
                    <br />
                    You can also{' '}
                    <Button
                      variant="link"
                      type="button"
                      className="p-0"
                      onClick={(e) => showPDFForm(e)}>
                      upload a PDF
                    </Button>{' '}
                    instead.
                  </p>
                </div>
              )}

              <Button className="w-full" type="submit">
                {formState.submitting ? 'Creating Account…' : 'Create Account'}
              </Button>
              <Turnstile
                onSuccess={() => {
                  setProvedHuman(true);
                }}
                options={{
                  size: 'flexible'
                }}
                key={idempotencyKey}
                siteKey={turnstileKey}
              />
            </fieldset>
          </Form>

          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-2 text-muted-foreground">
                Already Registered?{' '}
                <Link to="/login" className="underline">
                  Login
                </Link>
              </span>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            By clicking register, you agree to our{' '}
            <Link
              to="/docs/terms-of-service"
              target="_blank"
              className="underline underline-offset-4 hover:text-primary">
              Terms of Service
            </Link>
            .
          </p>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            <Link
              to="/docs/privacy-security"
              target="_blank"
              className="hover:text-primary">
              🔒{' '}
              <span className="underline underline-offset-4">
                Learn about our privacy and security practices
              </span>
            </Link>
          </p>
        </div>
        <div className="hidden w-[400px] flex-col border-l-2 border-black p-10 lg:block">
          <blockquote className="mt-6 flex flex-grow flex-col">
            <div className="relative flex-grow text-lg">
              <svg
                fill="currentColor"
                viewBox="0 0 32 32"
                className="absolute left-0 top-0 h-8 w-8 -translate-x-3 -translate-y-2 transform text-gray-300">
                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
              </svg>
              <p className="relative">
                I could see our sales enablement team really leveraging this &ndash;
                they’re spending a lot of time in the platform and they have a lot of
                questions. tia can give them exactly what they need, and not just blanket
                statements, but answers that are targeted for individuals and their needs
                at that moment.&rdquo;
              </p>
            </div>
            <div className="mt-8">
              <div className="flex items-start">
                <div className="inline-flex flex-shrink-0 rounded-full border-2 border-white">
                  <img alt="" src="/blockquote.jpeg" className="h-12 w-12 rounded-full" />
                </div>
                <div className="ml-4">
                  <div className="text-base font-medium">Sr. Instructional Designer</div>
                  <div className="text-base font-medium">BitSight</div>
                </div>
              </div>
            </div>
          </blockquote>
        </div>
      </div>

      <Toaster />
    </>
  );
}
